import React, { useState, useEffect, useRef, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { CertificateService, AdminService } from '../services/APIService';
import { CERT_COMPONENTS } from './CertTemplates';

const typeLabels = { appointment: 'Appointment', registration: 'Registration', experience: 'Experience', cultural: 'Cultural' };
const typeColors = { appointment: 'bg-blue-100 text-blue-700', registration: 'bg-green-100 text-green-700', experience: 'bg-red-100 text-red-700', cultural: 'bg-purple-100 text-purple-700' };
const typeIcons = { appointment: '👔', registration: '📋', experience: '⭐', cultural: '🎭' };

// Map API field names (snake_case from DB) to the format CertTemplates expects
const toTemplateData = (cert, bgImage) => ({
  name: cert.recipient_name,
  certNo: cert.cert_no,
  issueDate: cert.issue_date?.split('T')[0] || cert.issue_date,
  role: cert.role_title || '',
  fromDate: cert.from_date?.split('T')[0] || '',
  toDate: cert.to_date?.split('T')[0] || '',
  eventName: cert.event_name || '',
  place: cert.place || '',
  image: cert.cert_type === 'idcard' ? null : (bgImage || null),
});

const MyCertificates = () => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [previewCert, setPreviewCert] = useState(null);
  const [certBgData, setCertBgData] = useState(null);
  const printRef = useRef(null);

  useEffect(() => {
    CertificateService.getMy()
      .then(data => setCerts(Array.isArray(data) ? data : data.data || []))
      .catch(err => setError(err.message || err.error || 'Failed to load certificates'))
      .finally(() => setLoading(false));
    AdminService.getCertBgImage().then(d => setCertBgData(d.imageData || null));
  }, []);

  const downloadPDF = useCallback(async (cert) => {
    setDownloading(cert.id);
    setPreviewCert(cert);
    await new Promise(r => setTimeout(r, 300));
    await document.fonts.ready;
    try {
      const el = document.getElementById('cert-print-area');
      if (!el) throw new Error('Template not found');
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: null });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [842, 595] });
      pdf.addImage(imgData, 'PNG', 0, 0, 842, 595);
      pdf.save(`${cert.cert_no.replace(/\//g, '-')}_${cert.recipient_name}.pdf`);
    } catch (err) {
      alert('PDF generation failed: ' + err.message);
    } finally {
      setDownloading(null);
      setPreviewCert(null);
    }
  }, []);

  const CertComp = previewCert ? (CERT_COMPONENTS[previewCert.cert_type] || CERT_COMPONENTS.appointment) : null;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Certificates</h1>
          <p className="text-gray-500 mt-2">Official certificates issued to you by Karigar Foundation</p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 text-center">
            <p className="font-semibold">Could not load certificates</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {!loading && !error && certs.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
            <div className="text-6xl mb-4">🏅</div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No certificates yet</h2>
            <p className="text-gray-400">Certificates issued to you by admin will appear here.</p>
          </div>
        )}

        {!loading && certs.length > 0 && (
          <div className="space-y-4">
            {certs.map(cert => (
              <div key={cert.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-center justify-between gap-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4 min-w-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl flex-shrink-0 ${typeColors[cert.cert_type] || 'bg-gray-100 text-gray-500'}`}>
                    {typeIcons[cert.cert_type] || '📄'}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{cert.recipient_name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[cert.cert_type] || 'bg-gray-100 text-gray-600'}`}>
                        {typeLabels[cert.cert_type] || cert.cert_type}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 font-mono">{cert.cert_no}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Issued: {cert.issue_date?.split('T')[0] || cert.issue_date}
                      {cert.role_title && <span className="ml-2">· {cert.role_title}</span>}
                      {cert.event_name && <span className="ml-2">· {cert.event_name}</span>}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => downloadPDF(cert)}
                  disabled={downloading === cert.id}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${downloading === cert.id ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'}`}
                >
                  {downloading === cert.id ? <><span className="animate-spin">⏳</span> Generating...</> : <>⬇ Download PDF</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hidden off-screen render area for PDF generation */}
      {previewCert && CertComp && (
        <div style={{ position: 'fixed', left: -9999, top: 0, zIndex: -1 }} ref={printRef}>
          <div id="cert-print-area">
            <CertComp data={toTemplateData(previewCert, certBgData)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCertificates;
